import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Brain, Crown, Mail, ArrowLeft, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const { login, setUserData } = useAuth();
  const [mode, setMode] = useState('choose'); // choose | register | signin | verify-sent | forgot | reset-sent
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState('');

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const resp = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Registration failed');
      setVerifyEmail(email);
      setMode('verify-sent');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const resp = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        // If the server says the email isn't verified, surface a resend-verification shortcut
        if (resp.status === 403 && /verify your email/i.test(data.detail || '')) {
          setVerifyEmail(email);
        }
        throw new Error(data.detail || 'Login failed');
      }
      setUserData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendFromLogin = async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${API}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verifyEmail }),
      });
      const data = await resp.json();
      if (resp.ok) {
        setError('');
        setMode('verify-sent');
      } else {
        setError(data.detail || 'Failed to send verification email');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      await fetch(`${API}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verifyEmail }),
      });
      setError('');
    } catch { /* ignore */ }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const resp = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Request failed');
      setMode('reset-sent');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setError('');
    setName('');
    setEmail('');
    setPassword('');
    setVerifyEmail('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-600 to-cyan-500 flex items-center justify-center mx-auto mb-4">
            <Brain className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-purple-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent mb-2">
            NeuroWave One
          </h1>
          <p className="text-white/50 text-sm">Advanced Brainwave Entrainment</p>
        </div>

        {/* Auth Card */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 sm:p-8 mb-6">

          {/* Choose Method */}
          {mode === 'choose' && (
            <>
              <h2 className="text-white text-lg font-semibold text-center mb-2" data-testid="login-title">
                Sign in to get started
              </h2>
              <p className="text-white/40 text-xs text-center mb-6">
                22-day free trial included — no credit card required
              </p>

              <Button
                onClick={login}
                className="w-full bg-white hover:bg-gray-100 text-gray-900 font-medium py-5 rounded-xl flex items-center justify-center gap-3 text-sm mb-3"
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </Button>

              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-white/30 text-xs">or</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              <Button
                onClick={() => { resetForm(); setMode('register'); }}
                variant="outline"
                className="w-full border-white/10 !text-white hover:bg-white/5 bg-transparent py-5 rounded-xl flex items-center justify-center gap-3 text-sm mb-3"
                data-testid="email-signup-btn"
              >
                <Mail className="w-5 h-5" />
                Sign up with Email
              </Button>

              <p className="text-center text-white/30 text-xs mt-3">
                Already have an account?{' '}
                <button onClick={() => { resetForm(); setMode('signin'); }} className="text-purple-400 hover:text-purple-300" data-testid="switch-to-signin">
                  Sign in
                </button>
              </p>
            </>
          )}

          {/* Register Form */}
          {mode === 'register' && (
            <>
              <button onClick={() => { resetForm(); setMode('choose'); }} className="flex items-center gap-1 text-white/40 hover:text-white/60 text-sm mb-4">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <h2 className="text-white text-lg font-semibold mb-4" data-testid="register-title">Create your account</h2>
              <form onSubmit={handleRegister} className="space-y-3">
                <input
                  type="text" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 text-white placeholder-white/30 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-purple-500/50"
                  data-testid="register-name" required
                />
                <input
                  type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 text-white placeholder-white/30 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-purple-500/50"
                  data-testid="register-email" required
                />
                <input
                  type="password" placeholder="Password (min 6 characters)" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 text-white placeholder-white/30 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-purple-500/50"
                  data-testid="register-password" required minLength={6}
                />
                {error && <p className="text-red-400 text-xs" data-testid="auth-error">{error}</p>}
                <Button
                  type="submit" disabled={loading}
                  className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white py-5 rounded-xl font-semibold text-sm"
                  data-testid="register-submit"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Account'}
                </Button>
              </form>
              <p className="text-center text-white/30 text-xs mt-4">
                Already have an account?{' '}
                <button onClick={() => { resetForm(); setMode('signin'); }} className="text-purple-400 hover:text-purple-300">Sign in</button>
              </p>
            </>
          )}

          {/* Sign In Form */}
          {mode === 'signin' && (
            <>
              <button onClick={() => { resetForm(); setMode('choose'); }} className="flex items-center gap-1 text-white/40 hover:text-white/60 text-sm mb-4">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <h2 className="text-white text-lg font-semibold mb-4" data-testid="signin-title">Sign in</h2>
              <form onSubmit={handleLogin} className="space-y-3">
                <input
                  type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 text-white placeholder-white/30 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-purple-500/50"
                  data-testid="signin-email" required
                />
                <input
                  type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 text-white placeholder-white/30 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-purple-500/50"
                  data-testid="signin-password" required
                />
                {error && <p className="text-red-400 text-xs" data-testid="auth-error">{error}</p>}
                {verifyEmail && /verify your email/i.test(error) && (
                  <button
                    type="button"
                    onClick={handleResendFromLogin}
                    disabled={loading}
                    className="text-purple-400 hover:text-purple-300 text-xs underline underline-offset-2"
                    data-testid="signin-resend-verification-link"
                  >
                    Resend verification email
                  </button>
                )}
                <Button
                  type="submit" disabled={loading}
                  className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white py-5 rounded-xl font-semibold text-sm"
                  data-testid="signin-submit"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign In'}
                </Button>
              </form>
              <p className="text-center text-white/30 text-xs mt-4">
                <button onClick={() => { resetForm(); setMode('forgot'); }} className="text-purple-400 hover:text-purple-300" data-testid="forgot-password-link">
                  Forgot password?
                </button>
              </p>
              <p className="text-center text-white/30 text-xs mt-2">
                Don't have an account?{' '}
                <button onClick={() => { resetForm(); setMode('register'); }} className="text-purple-400 hover:text-purple-300">Sign up</button>
              </p>
            </>
          )}

          {/* Forgot Password */}
          {mode === 'forgot' && (
            <>
              <button onClick={() => { resetForm(); setMode('signin'); }} className="flex items-center gap-1 text-white/40 hover:text-white/60 text-sm mb-4">
                <ArrowLeft className="w-4 h-4" /> Back to sign in
              </button>
              <h2 className="text-white text-lg font-semibold mb-2" data-testid="forgot-title">Reset your password</h2>
              <p className="text-white/40 text-xs mb-4">Enter your email and we'll send you a reset link.</p>
              <form onSubmit={handleForgotPassword} className="space-y-3">
                <input
                  type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 text-white placeholder-white/30 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-purple-500/50"
                  data-testid="forgot-email" required
                />
                {error && <p className="text-red-400 text-xs" data-testid="auth-error">{error}</p>}
                <Button
                  type="submit" disabled={loading}
                  className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white py-5 rounded-xl font-semibold text-sm"
                  data-testid="forgot-submit"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Reset Link'}
                </Button>
              </form>
            </>
          )}

          {/* Reset Link Sent */}
          {mode === 'reset-sent' && (
            <div className="text-center" data-testid="reset-sent-screen">
              <div className="w-14 h-14 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-7 h-7 text-purple-400" />
              </div>
              <h2 className="text-white text-lg font-semibold mb-2">Check your email</h2>
              <p className="text-white/50 text-sm mb-4">
                If an account exists for <strong className="text-white/70">{email}</strong>, we've sent a password reset link.
              </p>
              <p className="text-white/30 text-xs mb-4">The link expires in 1 hour.</p>
              <p className="text-white/30 text-xs mt-4">
                <button onClick={() => { resetForm(); setMode('signin'); }} className="text-purple-400 hover:text-purple-300">
                  Back to sign in
                </button>
              </p>
            </div>
          )}

          {/* Verify Email Sent */}
          {mode === 'verify-sent' && (
            <div className="text-center" data-testid="verify-sent-screen">
              <div className="w-14 h-14 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-7 h-7 text-purple-400" />
              </div>
              <h2 className="text-white text-lg font-semibold mb-2">Check your email</h2>
              <p className="text-white/50 text-sm mb-4">
                We sent a verification link to <strong className="text-white/70">{verifyEmail}</strong>
              </p>
              <p className="text-white/30 text-xs mb-4">Click the link in the email to activate your account.</p>
              <Button
                onClick={handleResend} disabled={loading}
                variant="outline" className="border-white/10 text-white/50 hover:bg-white/5 text-xs"
                data-testid="resend-verification-btn"
              >
                {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                Resend verification email
              </Button>
              <p className="text-white/30 text-xs mt-4">
                <button onClick={() => { resetForm(); setMode('signin'); }} className="text-purple-400 hover:text-purple-300">
                  Back to sign in
                </button>
              </p>
            </div>
          )}
        </div>

        {/* Trial Badge */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/20 text-purple-300 px-4 py-2 rounded-full text-xs">
            <Crown className="w-3.5 h-3.5" />
            22-day free premium trial — then $9.99 lifetime
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
